import React, { useState, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Badge } from "primereact/badge";
import { InputText } from "primereact/inputtext";
import { Dialog } from "primereact/dialog";
import { makeRequest2 } from "../../../../../utils/makeRequest";
import { toastify } from "../../../../../utils/toast";
import { communicationBaseRoute } from "../../../../../utils/urls";
import ViewSMSModal from "./ViewSMSModal";

function QueuedSMSTab({ facilityId }) {
  const [queuedSMS, setQueuedSMS] = useState([]);
  const [loading, setLoading] = useState(false);
  const [globalFilter, setGlobalFilter] = useState("");
  const [selectedSMS, setSelectedSMS] = useState(null);
  const [selectedSMSList, setSelectedSMSList] = useState([]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [showReleaseConfirm, setShowReleaseConfirm] = useState(false);
  const [showBulkReleaseConfirm, setShowBulkReleaseConfirm] = useState(false);
  const [showReleaseAllConfirm, setShowReleaseAllConfirm] = useState(false);
  const [smsToDelete, setSmsToDelete] = useState(null);
  const [smsToRelease, setSmsToRelease] = useState(null);
  const [releaseLoading, setReleaseLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [communicationStatus, setCommunicationStatus] = useState(null);
  const [sendProgress, setSendProgress] = useState(0);
  const [currentlySending, setCurrentlySending] = useState(false);
  const [totalToSend, setTotalToSend] = useState(0);
  const [successCount, setSuccessCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);

  // Fetch queued SMS
  const fetchQueuedSMS = async () => {
    try {
      setLoading(true);
      const response = await makeRequest2(
        `${communicationBaseRoute}/${facilityId}/sms`,
        "GET",
      );

      // Debug logging
      console.log("SMS API Response:", response);
      console.log("SMS Response Data:", response.data);
      console.log("Is response.data an array?", Array.isArray(response.data));

      if (response.success) {
        // Handle the specific API response structure
        const smsData = response.data;
        if (Array.isArray(smsData)) {
          setQueuedSMS(smsData);
        } else if (smsData && typeof smsData === "object") {
          // Check for different possible response formats including smsMessages
          const smsMessages =
            smsData.smsMessages || smsData.sms || smsData.data || [];
          console.log("Extracted SMS:", smsMessages);
          setQueuedSMS(Array.isArray(smsMessages) ? smsMessages : []);
        } else {
          console.log("Setting empty array - data is not array or object");
          setQueuedSMS([]);
        }
      } else {
        throw new Error(response.error || "Failed to fetch queued SMS");
      }
    } catch (error) {
      console.error("Error fetching queued SMS:", error);
      toastify("Error fetching queued SMS: " + error.message, "error");
      setQueuedSMS([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Fetch communication status
  const fetchCommunicationStatus = async () => {
    try {
      const response = await makeRequest2(
        `${communicationBaseRoute}/${facilityId}/status`,
        "GET",
      );

      if (response.success) {
        setCommunicationStatus(response.data);
      }
    } catch (error) {
      console.error("Error fetching communication status:", error);
    }
  };

  // Release single SMS - USES DIRECT ENDPOINT
  const releaseSMS = async (smsId) => {
    try {
      // Check communication status before releasing
      if (communicationStatus && communicationStatus.smsStatus === "hold") {
        toastify(
          "Cannot release SMS: Communication is on hold for this facility",
          "error",
        );
        return;
      }

      setReleaseLoading(true);
      console.log(
        `🚀 Frontend: Releasing single SMS ${smsId} via direct endpoint`,
      );

      const response = await makeRequest2(
        `${communicationBaseRoute}/${facilityId}/sms/${smsId}/release`,
        "POST",
      );

      console.log("Single SMS release response:", response);

      if (response.success) {
        toastify("SMS released and sent successfully", "success");
        setShowReleaseConfirm(false);
        setSmsToRelease(null);
        fetchQueuedSMS(); // Refresh the list
      } else {
        toastify(
          "Failed to release SMS: " + (response.error || response.message),
          "warning",
        );
        fetchQueuedSMS(); // Refresh to show updated status
      }
    } catch (error) {
      console.error("Error releasing single SMS:", error);
      toastify("Error releasing SMS: " + error.message, "error");
    } finally {
      setReleaseLoading(false);
    }
  };

  // Function to send SMS one by one with a delay
  const timedReleaseSMS = async (smsIds) => {
    try {
      if (smsIds.length === 0) {
        toastify("No valid SMS IDs found", "error");
        return;
      }

      // Close any open modals before showing progress
      setShowBulkReleaseConfirm(false);

      setCurrentlySending(true);
      setTotalToSend(smsIds.length);
      setSendProgress(0);
      setSuccessCount(0);
      setFailedCount(0);

      for (let i = 0; i < smsIds.length; i++) {
        const smsId = smsIds[i];

        try {
          // Send SMS using direct endpoint
          const response = await makeRequest2(
            `${communicationBaseRoute}/${facilityId}/sms/${smsId}/release`,
            "POST",
          );

          if (response.success) {
            setSuccessCount((prev) => prev + 1);
          } else {
            setFailedCount((prev) => prev + 1);
            console.error(
              "Failed to release SMS:",
              smsId,
              response.error || response.message,
            );
          }
        } catch (error) {
          setFailedCount((prev) => prev + 1);
          console.error("Error releasing SMS:", smsId, error);
        }

        // Update progress
        setSendProgress(Math.round(((i + 1) / smsIds.length) * 100));

        // Don't add delay after the last item
        if (i < smsIds.length - 1) {
          // Wait 2 seconds before next send
          await new Promise((resolve) => setTimeout(resolve, 2000));
        }
      }

      // Show final result
      toastify(
        `SMS release completed: ${successCount} successful, ${failedCount} failed out of ${smsIds.length} SMS`,
        successCount > 0 ? "success" : "warning",
      );

      fetchQueuedSMS(); // Refresh the list
    } finally {
      // Hide progress bar and reset
      setCurrentlySending(false);
      setTimeout(() => {
        setSendProgress(0);
        setSuccessCount(0);
        setFailedCount(0);
      }, 1000); // Delay reset to allow animation to complete
    }
  };

  // Bulk release SMS - USES DIRECT ENDPOINT WITH DELAY
  const bulkReleaseSMS = async () => {
    try {
      // Check communication status before releasing
      if (communicationStatus === "HOLD") {
        toastify(
          "Cannot release SMS: Communication is on hold for this facility",
          "error",
        );
        return;
      }

      console.log(
        "🚀 Frontend: Selected SMS List for bulk release:",
        selectedSMSList,
      );

      if (!selectedSMSList || selectedSMSList.length === 0) {
        toastify("No SMS selected for release", "warning");
        return;
      }

      setReleaseLoading(true);

      // Extract SMS IDs with proper validation
      const smsIds = selectedSMSList
        .filter((sms) => sms && sms._id)
        .map((sms) => sms._id);

      console.log(
        "📦 Frontend: SMS IDs to release via timed direct send:",
        smsIds,
      );

      if (smsIds.length === 0) {
        toastify("No valid SMS IDs found", "error");
        setReleaseLoading(false);
        return;
      }

      // Use the timed release method instead of bulk endpoint
      await timedReleaseSMS(smsIds);

      // Clear selection and refresh
      setSelectedSMSList([]);
    } catch (error) {
      console.error("Error in bulkReleaseSMS:", error);
      toastify("Error releasing SMS: " + error.message, "error");
    } finally {
      setReleaseLoading(false);
    }
  };

  // Release all SMS for facility - USES DIRECT ENDPOINT WITH DELAY
  const releaseAllSMS = async () => {
    try {
      // Check communication status before releasing
      if (communicationStatus === "HOLD") {
        toastify(
          "Cannot release SMS: Communication is on hold for this facility",
          "error",
        );
        return;
      }

      console.log("🚀 Frontend: Releasing all SMS via timed direct send");
      setReleaseLoading(true);
      setShowReleaseAllConfirm(false); // Close modal immediately

      // First get all SMS messages
      const response = await makeRequest2(
        `${communicationBaseRoute}/${facilityId}/sms`,
        "GET",
      );

      if (!response.success) {
        toastify(
          "Failed to fetch queued SMS: " + (response.error || response.message),
          "error",
        );
        return;
      }

      // Handle the specific API response structure
      let allSMS = [];
      if (Array.isArray(response.data)) {
        allSMS = response.data;
      } else if (response.data && typeof response.data === "object") {
        // Check for different possible response formats
        allSMS =
          response.data.smsMessages ||
          response.data.sms ||
          response.data.data ||
          [];
      }

      // Filter to only get pending SMS
      const pendingSMS = allSMS.filter(
        (sms) => sms.status === "pending" || !sms.status,
      );

      if (pendingSMS.length === 0) {
        toastify("No pending SMS to release", "info");
        setReleaseLoading(false);
        return;
      }

      // Extract SMS IDs
      const smsIds = pendingSMS
        .filter((sms) => sms && sms._id)
        .map((sms) => sms._id);

      // Use the timed release method
      await timedReleaseSMS(smsIds);

      setSelectedSMSList([]);
    } catch (error) {
      console.error("Error in releaseAllSMS:", error);
      toastify("Error releasing all SMS: " + error.message, "error");
    } finally {
      setReleaseLoading(false);
    }
  };

  // Delete SMS from queue
  const deleteQueuedSMS = async (smsId) => {
    try {
      const response = await makeRequest2(
        `${communicationBaseRoute}/${facilityId}/sms/${smsId}`,
        "DELETE",
      );

      if (response.success) {
        toastify("SMS deleted from queue successfully", "success");
        fetchQueuedSMS(); // Refresh the list
      } else {
        throw new Error(response.error || "Failed to delete SMS");
      }
    } catch (error) {
      toastify("Error deleting SMS: " + error.message, "error");
    }
  };

  // Bulk delete SMS
  const bulkDeleteSMS = async () => {
    try {
      setLoading(true);
      const deletePromises = selectedSMSList.map((sms) =>
        makeRequest2(
          `${communicationBaseRoute}/${facilityId}/sms/${sms._id}`,
          "DELETE",
        ),
      );

      const results = await Promise.allSettled(deletePromises);
      const successful = results.filter(
        (result) => result.status === "fulfilled" && result.value.success,
      ).length;
      const failed = results.length - successful;

      if (successful > 0) {
        toastify(
          `${successful} SMS deleted successfully${failed > 0 ? `, ${failed} failed` : ""}`,
          "success",
        );
      }
      if (failed > 0 && successful === 0) {
        toastify(`Failed to delete ${failed} SMS`, "error");
      }

      setSelectedSMSList([]);
      fetchQueuedSMS();
    } catch (error) {
      toastify("Error deleting SMS: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  // Handle delete confirmation
  const confirmDelete = (sms) => {
    setSmsToDelete(sms);
    setShowDeleteConfirm(true);
  };

  const handleDelete = () => {
    if (smsToDelete) {
      deleteQueuedSMS(smsToDelete._id);
      setShowDeleteConfirm(false);
      setSmsToDelete(null);
    }
  };

  // Handle bulk delete confirmation
  const confirmBulkDelete = () => {
    if (selectedSMSList.length > 0) {
      setShowBulkDeleteConfirm(true);
    }
  };

  const handleBulkDelete = () => {
    bulkDeleteSMS();
    setShowBulkDeleteConfirm(false);
  };

  // Handle release confirmation
  const confirmRelease = (sms) => {
    setSmsToRelease(sms);
    setShowReleaseConfirm(true);
  };

  const handleRelease = () => {
    if (smsToRelease) {
      releaseSMS(smsToRelease._id);
    }
  };

  // Handle bulk release confirmation
  const confirmBulkRelease = () => {
    console.log("confirmBulkRelease called, selectedSMSList:", selectedSMSList);
    if (selectedSMSList && selectedSMSList.length > 0) {
      setShowBulkReleaseConfirm(true);
    } else {
      toastify("Please select SMS to release", "warning");
    }
  };

  const handleBulkRelease = () => {
    console.log("handleBulkRelease called");
    bulkReleaseSMS();
  };

  // Handle release all confirmation
  const confirmReleaseAll = () => {
    setShowReleaseAllConfirm(true);
  };

  const handleReleaseAll = () => {
    releaseAllSMS();
  };

  // Handle view SMS
  const handleViewSMS = (sms) => {
    setSelectedSMS(sms);
    setShowViewModal(true);
  };

  // Handle refresh
  const handleRefresh = () => {
    setRefreshing(true);
    fetchQueuedSMS();
  };

  // Date formatter
  const formatDate = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleString();
  };

  // Message column template
  const messageBodyTemplate = (rowData) => {
    const message = rowData.sms?.message || "-";
    return (
      <div className="d-flex align-items-center">
        <span className="text-truncate" style={{ maxWidth: "250px" }}>
          {message}
        </span>
        {message.length > 50 && (
          <i className="ti ti-dots ms-2" title="Click to view full message" />
        )}
      </div>
    );
  };

  // Recipient column template
  const recipientBodyTemplate = (rowData) => {
    return rowData.sms?.to || "-";
  };

  // Status column template
  const statusBodyTemplate = (rowData) => {
    const status = rowData.status || "pending";
    let severity = "warning";
    let icon = "ti ti-clock";

    switch (status) {
      case "pending":
        severity = "warning";
        icon = "ti ti-clock";
        break;
      case "failed":
        severity = "danger";
        icon = "ti ti-alert-triangle";
        break;
      case "sent":
        severity = "success";
        icon = "ti ti-check";
        break;
      default:
        severity = "info";
        icon = "ti ti-info-circle";
    }

    return (
      <div className="d-flex align-items-center gap-1">
        <i className={icon} style={{ fontSize: "12px" }}></i>
        <Badge value={status} severity={severity} />
      </div>
    );
  };

  // Message length template
  const messageLengthTemplate = (rowData) => {
    const message = rowData.sms?.message || "";
    const length = message.length;
    const segments = Math.ceil(length / 160);

    return (
      <div className="text-center">
        <span className="d-block">{length}</span>
        <small className="text-muted">({segments} SMS)</small>
      </div>
    );
  };

  // Actions column template
  const actionsBodyTemplate = (rowData) => {
    const isPending = rowData.status === "pending" || !rowData.status;

    return (
      <div className="d-flex gap-1">
        <Button
          icon="ti ti-eye"
          size="small"
          className="p-button-outlined p-button-info"
          onClick={() => handleViewSMS(rowData)}
          tooltip="View SMS"
          tooltipOptions={{ position: "top" }}
        />
        {isPending && (
          <Button
            icon="ti ti-send"
            size="small"
            className="p-button-outlined p-button-success"
            onClick={() => confirmRelease(rowData)}
            tooltip="Release SMS"
            tooltipOptions={{ position: "top" }}
            loading={releaseLoading}
          />
        )}
        <Button
          icon="ti ti-trash"
          size="small"
          className="p-button-outlined p-button-danger"
          onClick={() => confirmDelete(rowData)}
          tooltip="Delete SMS"
          tooltipOptions={{ position: "top" }}
        />
      </div>
    );
  };

  // Table header
  const renderHeader = () => {
    return (
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
        <div className="d-flex align-items-center gap-3">
          <h6 className="mb-0">
            <i className="ti ti-message-circle me-2"></i>
            Queued SMS ({queuedSMS.length})
          </h6>
          {queuedSMS.filter((sms) => sms.status === "pending" || !sms.status)
            .length > 0 && (
            <Button
              icon="ti ti-send-2"
              size="small"
              className="p-button-info p-button-outlined"
              onClick={confirmReleaseAll}
              tooltip="Release All Pending"
              tooltipOptions={{ position: "top" }}
              style={{ height: "32px" }}
              loading={releaseLoading}
              label="Release All"
            />
          )}
          {selectedSMSList.length > 0 && (
            <div className="d-flex align-items-center gap-2">
              <span className="badge bg-primary fs-6">
                {selectedSMSList.length} selected
              </span>
              <Button
                icon="ti ti-send"
                size="small"
                className="p-button-success p-button-outlined"
                onClick={confirmBulkRelease}
                tooltip="Release Selected"
                tooltipOptions={{ position: "top" }}
                style={{ height: "32px" }}
                loading={releaseLoading}
                disabled={selectedSMSList.every(
                  (sms) => sms.status !== "pending" && sms.status !== undefined,
                )}
              />
              <Button
                icon="ti ti-trash"
                size="small"
                className="p-button-danger p-button-outlined"
                onClick={confirmBulkDelete}
                tooltip="Delete Selected"
                tooltipOptions={{ position: "top" }}
                style={{ height: "32px" }}
              />
            </div>
          )}
        </div>
        <div className="d-flex align-items-center gap-2">
          <div className="p-input-icon-left">
            <i
              className="ti ti-search"
              style={{ left: "12px", color: "#6c757d" }}
            />
            <InputText
              type="search"
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              placeholder="Search SMS..."
              className="p-inputtext-sm"
              style={{
                paddingLeft: "35px",
                width: "250px",
                height: "32px",
              }}
            />
          </div>
          <Button
            icon={refreshing ? "ti ti-loader animate-spin" : "ti ti-refresh"}
            size="small"
            className="p-button-outlined p-button-secondary"
            onClick={handleRefresh}
            disabled={refreshing}
            tooltip="Refresh"
            tooltipOptions={{ position: "top" }}
            style={{ height: "32px", width: "32px" }}
          />
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (facilityId) {
      fetchQueuedSMS();
      fetchCommunicationStatus();
    }
  }, [facilityId]);

  // Add debug logging for selection changes
  const handleSelectionChange = (e) => {
    console.log("🔄 Frontend: Selection changed:", e.value);
    console.log("Selection count:", e.value?.length || 0);
    setSelectedSMSList(e.value);
  };

  return (
    <div className="queued-sms-tab">
      {currentlySending && (
        <Card className="mb-3">
          <div className="send-progress-container">
            <h6 className="mb-2">
              <i className="ti ti-send me-2"></i>
              Sending SMS Messages
            </h6>
            <div className="progress mb-2" style={{ height: "20px" }}>
              <div
                className="progress-bar"
                role="progressbar"
                style={{ width: `${sendProgress}%` }}
                aria-valuenow={sendProgress}
                aria-valuemin="0"
                aria-valuemax="100"
              >
                {sendProgress}%
              </div>
            </div>
            <div className="d-flex justify-content-between">
              <span className="text-success">Success: {successCount}</span>
              <span className="text-danger">Failed: {failedCount}</span>
              <span className="text-primary">Total: {totalToSend}</span>
            </div>
          </div>
        </Card>
      )}
      <Card>
        <DataTable
          value={Array.isArray(queuedSMS) ? queuedSMS : []}
          loading={loading}
          header={renderHeader()}
          globalFilter={globalFilter}
          emptyMessage="No queued SMS found"
          paginator
          rows={10}
          rowsPerPageOptions={[5, 10, 25, 50]}
          responsiveLayout="scroll"
          className="p-datatable-sm"
          selection={selectedSMSList}
          onSelectionChange={handleSelectionChange}
          dataKey="_id"
        >
          <Column selectionMode="multiple" headerStyle={{ width: "3em" }} />
          <Column
            field="sms.message"
            header="Message"
            body={messageBodyTemplate}
            sortable
            style={{ minWidth: "250px" }}
          />
          <Column
            field="sms.to"
            header="To"
            body={recipientBodyTemplate}
            sortable
            style={{ minWidth: "130px" }}
          />
          <Column
            header="Length"
            body={messageLengthTemplate}
            style={{ minWidth: "80px" }}
          />
          <Column
            field="status"
            header="Status"
            body={statusBodyTemplate}
            sortable
            style={{ minWidth: "100px" }}
          />
          <Column
            field="createdAt"
            header="Queued At"
            body={(rowData) => formatDate(rowData.createdAt)}
            sortable
            style={{ minWidth: "150px" }}
          />
          <Column
            header="Actions"
            body={actionsBodyTemplate}
            exportable={false}
            style={{ minWidth: "120px" }}
          />
        </DataTable>
      </Card>

      {/* View SMS Modal */}
      <ViewSMSModal
        visible={showViewModal}
        onHide={() => {
          setShowViewModal(false);
          setSelectedSMS(null);
        }}
        sms={selectedSMS}
        facilityId={facilityId}
        onSMSUpdated={fetchQueuedSMS}
        onSMSDeleted={fetchQueuedSMS}
      />

      {/* Release Single SMS Confirmation Dialog */}
      <Dialog
        visible={showReleaseConfirm}
        style={{ width: "450px" }}
        header="Confirm Release SMS"
        modal
        footer={
          <div>
            <Button
              label="Cancel"
              icon="ti ti-x"
              onClick={() => setShowReleaseConfirm(false)}
              className="p-button-text"
            />
            <Button
              label="Release"
              icon="ti ti-send"
              className="p-button-success"
              onClick={handleRelease}
              loading={releaseLoading}
              disabled={communicationStatus?.smsStatus === "hold"}
            />
          </div>
        }
        onHide={() => setShowReleaseConfirm(false)}
      >
        <div className="confirmation-content">
          <i
            className="ti ti-send"
            style={{ fontSize: "2rem", color: "var(--green-500)" }}
          />
          <span className="ms-3">
            Are you sure you want to release this SMS?
            <br />
            <strong>To:</strong> {smsToRelease?.sms?.to}
            <br />
            <strong>Message:</strong>{" "}
            {smsToRelease?.sms?.message?.substring(0, 100)}
            {smsToRelease?.sms?.message?.length > 100 ? "..." : ""}
            <br />
            <small className="text-muted">
              The SMS will be sent immediately using direct endpoint.
              {communicationStatus?.smsStatus === "hold" && (
                <span className="text-danger"> (Currently on hold)</span>
              )}
            </small>
          </span>
        </div>
      </Dialog>

      {/* Release All SMS Confirmation Dialog */}
      <Dialog
        visible={showReleaseAllConfirm}
        style={{ width: "450px" }}
        header="Confirm Release All SMS"
        modal
        footer={
          <div>
            <Button
              label="Cancel"
              icon="ti ti-x"
              onClick={() => setShowReleaseAllConfirm(false)}
              className="p-button-text"
            />
            <Button
              label="Release All"
              icon="ti ti-send"
              onClick={handleReleaseAll}
              className="p-button-success"
              loading={releaseLoading}
              disabled={communicationStatus?.smsStatus === "hold"}
            />
          </div>
        }
        onHide={() => setShowReleaseAllConfirm(false)}
      >
        <div className="confirmation-content">
          <i
            className="ti ti-send-2"
            style={{ fontSize: "2rem", color: "var(--blue-500)" }}
          />
          <span className="ms-3">
            Are you sure you want to release ALL pending SMS for this facility?
            <br />
            <small className="text-muted">
              All pending SMS messages will be sent via external queue in
              batches of 10 with 2-second delays. Successfully sent messages
              will be removed from the queue, while failed messages will remain.
              {communicationStatus?.smsStatus === "hold" && (
                <span className="text-danger">
                  {" "}
                  (Cannot release: Communication is on hold)
                </span>
              )}
            </small>
          </span>
        </div>
      </Dialog>

      {/* Bulk Release Confirmation Dialog */}
      <Dialog
        visible={showBulkReleaseConfirm}
        style={{ width: "450px" }}
        header="Confirm Bulk Release"
        modal
        footer={
          <div>
            <Button
              label="Cancel"
              icon="ti ti-x"
              onClick={() => setShowBulkReleaseConfirm(false)}
              className="p-button-text"
            />
            <Button
              label="Release Selected"
              icon="ti ti-send"
              onClick={handleBulkRelease}
              className="p-button-success"
              loading={releaseLoading}
              disabled={communicationStatus?.smsStatus === "hold"}
            />
          </div>
        }
        onHide={() => setShowBulkReleaseConfirm(false)}
      >
        <div className="confirmation-content">
          <i
            className="ti ti-send"
            style={{ fontSize: "2rem", color: "var(--green-500)" }}
          />
          <span className="ms-3">
            Are you sure you want to release {selectedSMSList.length} selected
            SMS?
            <br />
            <small className="text-muted">
              SMS will be sent via external queue in batches of 10 with 2-second
              delays. Successfully sent SMS will be removed from the queue,
              while failed SMS will be marked as failed.
              {communicationStatus?.smsStatus === "hold" && (
                <span className="text-danger">
                  {" "}
                  (Cannot release: Communication is on hold)
                </span>
              )}
            </small>
          </span>
        </div>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog
        visible={showBulkDeleteConfirm}
        style={{ width: "450px" }}
        header="Confirm Bulk Delete"
        modal
        footer={
          <div>
            <Button
              label="Cancel"
              icon="ti ti-x"
              onClick={() => setShowBulkDeleteConfirm(false)}
              className="p-button-text"
            />
            <Button
              label="Delete All"
              icon="ti ti-trash"
              onClick={handleBulkDelete}
              className="p-button-danger"
              loading={loading}
            />
          </div>
        }
        onHide={() => setShowBulkDeleteConfirm(false)}
      >
        <div className="confirmation-content">
          <i
            className="ti ti-exclamation-triangle"
            style={{ fontSize: "2rem", color: "var(--orange-500)" }}
          />
          <span className="ms-3">
            Are you sure you want to delete {selectedSMSList.length} selected
            SMS from the queue?
            <br />
            <small className="text-muted">This action cannot be undone.</small>
          </span>
        </div>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        visible={showDeleteConfirm}
        style={{ width: "450px" }}
        header="Confirm Delete"
        modal
        footer={
          <div>
            <Button
              label="Cancel"
              icon="ti ti-x"
              onClick={() => setShowDeleteConfirm(false)}
              className="p-button-text"
            />
            <Button
              label="Delete"
              icon="ti ti-trash"
              onClick={handleDelete}
              className="p-button-danger"
            />
          </div>
        }
        onHide={() => setShowDeleteConfirm(false)}
      >
        <div className="confirmation-content">
          <i
            className="ti ti-exclamation-triangle"
            style={{ fontSize: "2rem", color: "var(--orange-500)" }}
          />
          <span className="ms-3">
            Are you sure you want to delete this SMS from the queue?
            <br />
            <strong>To:</strong> {smsToDelete?.sms?.to}
            <br />
            <strong>Message Preview:</strong>{" "}
            {smsToDelete?.sms?.message?.substring(0, 50)}...
          </span>
        </div>
      </Dialog>
    </div>
  );
}

export default QueuedSMSTab;
